import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {AIConditionSignal} from '../../promptToCardAgentV2.types';
import {ConditionCardScratchpadState} from '../../scratchpad/conditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import updateFallbackOutputWithDefaultValues from '../../utils/conditions/updateFallbackOutputWithDefaultValues';

const setSignalInputSchema = z.object({
  signal: z.custom<AIConditionSignal>().describe('The signal to set'),
});

export default function setSignalTool() {
  async function execute(
    params: ToolExecuteParams<typeof setSignalInputSchema, ConditionCardScratchpadState>,
  ) {
    const {signal} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.CONDITION,
    );

    if (!validatedScratchpad.card.availableSignals?.some((s) => s.signalKey === signal.signalKey)) {
      return toolResult(
        `The signal ${signal.signalKey} is not valid for the condition card. Valid signals are: ${validatedScratchpad.card.availableSignals?.map((s) => s.signalKey).join(', ')}`,
      );
    }

    validatedScratchpad.card.condKey = signal.signalKey;
    validatedScratchpad.card.condKeyType = signal.signalType;
    validatedScratchpad.card.categories = signal.categories.map((category) => category.value);
    validatedScratchpad.card.signalDescription = signal.signalName;
    validatedScratchpad.card.optionalOperators = signal.optionalOperators.map((operator) => ({
      value: operator.value,
      title: operator.title,
    }));
    validatedScratchpad.card.shouldQueryOptionalValues = signal.shouldQueryOptionalValues;

    // Update fallback output with default values based on the new signal type
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (output) => output.type === OutputType.Fallback,
    );

    if (fallbackOutput) {
      updateFallbackOutputWithDefaultValues(
        fallbackOutput,
        signal.signalType,
        validatedScratchpad.card.condOperator,
      );
    }

    return toolResult(`Signal set successfully: ${JSON.stringify(signal)}`);
  }

  return tool<typeof setSignalInputSchema, ConditionCardScratchpadState>({
    name: V2CardAgentToolName.SetSignal,
    displayName: 'Setting signal',
    description:
      "Following the user's prompt, set the signal for the condition card. Use the values returned from the ${CampaignSetupAgentToolName.SearchSignal} tool to set the signal for the condition card. Match the signal and operator to the user's prompt.",
    inputSchema: setSignalInputSchema,
    execute,
  });
}
